import * as RadixDialog from "@radix-ui/react-dialog";
import { Database, FileText, Home, PenLine, Search, Settings, ShieldCheck, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./Button";

const commands = [
  { label: "Home", detail: "Return to the ClearScope cover", to: "/", icon: Home },
  { label: "Post a job", detail: "Open the job form", to: "/post", icon: PenLine },
  { label: "Browse jobs", detail: "Scan the local board", to: "/jobs", icon: FileText },
  { label: "Safety", detail: "Review trust boundaries", to: "/safety", icon: ShieldCheck },
  { label: "Data controls", detail: "Export, import, or reset", to: "/data", icon: Database },
  { label: "Settings", detail: "Security and provider settings", to: "/settings", icon: Settings },
];

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }
    const timeout = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(timeout);
  }, [open]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) {
      return commands;
    }
    return commands.filter((command) =>
      [command.label, command.detail].join(" ").toLowerCase().includes(needle),
    );
  }, [query]);

  function run(to: string) {
    navigate(to);
    setOpen(false);
    setQuery("");
  }

  return (
    <RadixDialog.Root open={open} onOpenChange={setOpen}>
      <RadixDialog.Trigger asChild>
        <Button
          aria-label="Open command palette"
          className="hidden lg:inline-flex"
          iconLeft={<Search aria-hidden="true" className="h-4 w-4" strokeWidth={1.5} />}
          size="sm"
          variant="tonal"
        >
          Search
          <span className="rounded-[7px] border border-border bg-surface px-1.5 py-0.5 font-mono text-[0.6875rem] text-text-subtle">
            Ctrl K
          </span>
        </Button>
      </RadixDialog.Trigger>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="dialog-overlay fixed inset-0 z-50 bg-[var(--color-overlay)] backdrop-blur-2xl" />
        <RadixDialog.Content className="dialog-content fixed left-1/2 top-1/2 z-50 w-[min(42rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-[var(--radius-dialog)] border border-border bg-surface-elevated p-3 shadow-[var(--shadow-key-3)]">
          <div className="flex min-h-14 items-center gap-3 border-b border-border px-2 pb-3">
            <Search aria-hidden="true" className="h-5 w-5 text-text-subtle" strokeWidth={1.5} />
            <RadixDialog.Title className="sr-only">Command palette</RadixDialog.Title>
            <input
              ref={inputRef}
              className="min-h-14 flex-1 bg-transparent text-base text-text outline-none placeholder:text-text-subtle"
              placeholder="Search ClearScope"
              value={query}
              onChange={(event) => setQuery(event.currentTarget.value)}
            />
            <RadixDialog.Close asChild>
              <Button aria-label="Close command palette" size="sm" variant="ghost">
                <X aria-hidden="true" className="h-4 w-4" />
              </Button>
            </RadixDialog.Close>
          </div>
          <div className="grid max-h-[60vh] gap-1 overflow-auto pt-3">
            {filtered.map((command) => (
              <button
                key={command.to}
                className="flex min-h-16 items-center gap-3 rounded-[14px] px-3 text-left transition hover:bg-surface-muted focus-visible:bg-surface-muted"
                type="button"
                onClick={() => run(command.to)}
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-[12px] border border-border bg-surface-raised text-text-muted">
                  <command.icon aria-hidden="true" className="h-4 w-4" strokeWidth={1.5} />
                </span>
                <span>
                  <span className="block text-sm font-semibold text-text">{command.label}</span>
                  <span className="mt-1 block text-xs font-semibold text-text-subtle">
                    {command.detail}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
